// i18n configuration and utilities
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

// Supported languages configuration - English, Hebrew, Arabic are primary languages
export const SUPPORTED_LANGUAGES = {
  en: { name: 'English', englishName: 'English', dir: 'ltr', flag: '🇺🇸' },
  he: { name: 'עברית', englishName: 'Hebrew', dir: 'rtl', flag: '🇮🇱' },
  ar: { name: 'العربية', englishName: 'Arabic', dir: 'rtl', flag: '🇸🇦' },
  ru: { name: 'Русский', englishName: 'Russian', dir: 'ltr', flag: '🇷🇺' },
  fr: { name: 'Français', englishName: 'French', dir: 'ltr', flag: '🇫🇷' },
  es: { name: 'Español', englishName: 'Spanish', dir: 'ltr', flag: '🇪🇸' },
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

// RTL languages
export const RTL_LANGUAGES: SupportedLanguage[] = ['he', 'ar'];

export const isRTLLanguage = (lang: string): boolean => {
  return RTL_LANGUAGES.includes(lang as SupportedLanguage);
};

export const getLanguageDirection = (lang: string): 'rtl' | 'ltr' => {
  return isRTLLanguage(lang) ? 'rtl' : 'ltr';
};

// Default fallback translations (embedded for offline/initial load)
const fallbackTranslations = {
  he: {
    common: {
      loading: 'טוען...',
      save: 'שמור',
      cancel: 'ביטול',
      delete: 'מחק',
      edit: 'עריכה',
      add: 'הוסף',
      search: 'חיפוש',
      filter: 'סינון',
      actions: 'פעולות',
      status: 'סטטוס',
      active: 'פעיל',
      inactive: 'לא פעיל',
      yes: 'כן',
      no: 'לא',
      confirm: 'אישור',
      back: 'חזרה',
      next: 'הבא',
      previous: 'הקודם',
      close: 'סגור',
      submit: 'שלח',
      error: 'שגיאה',
      success: 'הצלחה',
      noResults: 'לא נמצאו תוצאות',
      language: 'שפה',
      selectLanguage: 'בחר שפה',
      profile: 'פרופיל',
      settings: 'הגדרות',
      logout: 'התנתקות',
      notifications: 'התראות',
      viewAll: 'הצג הכל',
      noNotifications: 'אין התראות',
      markAllRead: 'סמן הכל כנקרא',
      addWidgets: 'הוסף ווידג׳טים',
      createMeeting: 'צור פגישה',
      messages: 'הודעות',
      enterRoom: 'היכנס לחדר',
      schoolInfo: 'פרטי בית ספר',
      myClasses: 'הכיתות שלי',
      dailyHours: 'שעות עבודה יומיות',
      myStudents: 'התלמידים שלי',
      myTeachers: 'המורים שלי',
    },
    auth: {
      login: 'התחברות',
      logout: 'התנתקות',
      register: 'הרשמה',
      email: 'אימייל',
      password: 'סיסמה',
      confirmPassword: 'אימות סיסמה',
      firstName: 'שם פרטי',
      lastName: 'שם משפחה',
      forgotPassword: 'שכחת סיסמה?',
      rememberMe: 'זכור אותי',
      noAccount: 'אין לך חשבון?',
      haveAccount: 'יש לך חשבון?',
      loginSuccess: 'התחברת בהצלחה',
      registerSuccess: 'נרשמת בהצלחה',
      invalidCredentials: 'אימייל או סיסמה שגויים',
      signIn: 'התחבר',
      signUp: 'הירשם',
      welcomeBack: 'ברוך שובך',
      createAccount: 'צור חשבון חדש',
    },
    nav: {
      dashboard: 'לוח בקרה',
      courses: 'קורסים',
      myCourses: 'הקורסים שלי',
      meetings: 'פגישות',
      recordings: 'הקלטות',
      students: 'תלמידים',
      teachers: 'מורים',
      calendar: 'לוח שנה',
      schedule: 'לוח זמנים',
      grades: 'ציונים',
      settings: 'הגדרות',
      aiTeacher: 'מורה AI',
      finance: 'כספים',
      library: 'ספרייה',
      messages: 'הודעות',
      profile: 'פרופיל',
      tasks: 'משימות',
      academics: 'אקדמיה',
      marketing: 'שיווק',
      school: 'בית ספר',
    },
    courses: {
      title: 'קורסים',
      newCourse: 'קורס חדש',
      courseName: 'שם הקורס',
      courseCode: 'קוד קורס',
      description: 'תיאור',
      students: 'תלמידים',
      teachers: 'מורים',
      startDate: 'תאריך התחלה',
      endDate: 'תאריך סיום',
      status: 'סטטוס',
      draft: 'טיוטה',
      active: 'פעיל',
      archived: 'בארכיון',
      enrollments: 'נרשמים',
      sessions: 'מפגשים',
      materials: 'חומרים',
    },
    meetings: {
      title: 'פגישות',
      newMeeting: 'פגישה חדשה',
      joinMeeting: 'הצטרף לפגישה',
      startMeeting: 'התחל פגישה',
      endMeeting: 'סיים פגישה',
      scheduledStart: 'מתוזמן ל',
      host: 'מארח',
      participants: 'משתתפים',
      recording: 'הקלטה',
      startRecording: 'התחל הקלטה',
      stopRecording: 'עצור הקלטה',
      live: 'בשידור חי',
      scheduled: 'מתוזמן',
      ended: 'הסתיים',
      camera: 'מצלמה',
      microphone: 'מיקרופון',
      shareScreen: 'שיתוף מסך',
      chat: 'צ׳אט',
      leaveRoom: 'יציאה מהחדר',
    },
    finance: {
      title: 'כספים',
      revenue: 'הכנסות',
      expenses: 'הוצאות',
      balance: 'יתרה',
      invoices: 'חשבוניות',
      payments: 'תשלומים',
      subscription: 'מנוי',
      plan: 'תוכנית',
      billing: 'חיוב',
      payNow: 'שלם עכשיו',
      addToCart: 'הוסף לעגלה',
      cart: 'עגלה',
      checkout: 'לתשלום',
      total: 'סה"כ',
      subtotal: 'סכום ביניים',
      tax: 'מע"מ',
      discount: 'הנחה',
      paymentMethod: 'אמצעי תשלום',
      creditCard: 'כרטיס אשראי',
      connectStripe: 'חבר חשבון Stripe',
      stripeConnected: 'חשבון Stripe מחובר',
    },
    superAdmin: {
      title: 'ניהול על',
      schools: 'בתי ספר',
      users: 'משתמשים',
      subscriptions: 'מנויים',
      storage: 'אחסון',
      auditLogs: 'יומן פעילות',
      admins: 'מנהלי על',
      platformSettings: 'הגדרות פלטפורמה',
      pricingPlans: 'תוכניות מחיר',
      createPlan: 'צור תוכנית',
      editPlan: 'ערוך תוכנית',
      planName: 'שם התוכנית',
      monthlyPrice: 'מחיר חודשי',
      yearlyPrice: 'מחיר שנתי',
      features: 'תכונות',
      limits: 'מגבלות',
    },
    errors: {
      required: 'שדה חובה',
      invalidEmail: 'כתובת אימייל לא תקינה',
      minLength: 'מינימום {{min}} תווים',
      maxLength: 'מקסימום {{max}} תווים',
      passwordMismatch: 'הסיסמאות לא תואמות',
      serverError: 'שגיאת שרת, נסה שוב מאוחר יותר',
      networkError: 'שגיאת רשת, בדוק את החיבור שלך',
      unauthorized: 'אין לך הרשאה לבצע פעולה זו',
      notFound: 'הדף לא נמצא',
    },
  },
  en: {
    common: {
      loading: 'Loading...',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      add: 'Add',
      search: 'Search',
      filter: 'Filter',
      actions: 'Actions',
      status: 'Status',
      active: 'Active',
      inactive: 'Inactive',
      yes: 'Yes',
      no: 'No',
      confirm: 'Confirm',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      close: 'Close',
      submit: 'Submit',
      error: 'Error',
      success: 'Success',
      noResults: 'No results found',
      language: 'Language',
      selectLanguage: 'Select Language',
      profile: 'Profile',
      settings: 'Settings',
      logout: 'Logout',
      notifications: 'Notifications',
      viewAll: 'View All',
      noNotifications: 'No notifications',
      markAllRead: 'Mark all as read',
      addWidgets: 'Add Widgets',
      createMeeting: 'Create Meeting',
      messages: 'Messages',
      enterRoom: 'Enter Room',
      schoolInfo: 'School Info',
      myClasses: 'My Classes',
      dailyHours: 'Daily Working Hours',
      myStudents: 'My Students',
      myTeachers: 'My Teachers',
    },
    auth: {
      login: 'Login',
      logout: 'Logout',
      register: 'Register',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      firstName: 'First Name',
      lastName: 'Last Name',
      forgotPassword: 'Forgot Password?',
      rememberMe: 'Remember Me',
      noAccount: "Don't have an account?",
      haveAccount: 'Already have an account?',
      loginSuccess: 'Logged in successfully',
      registerSuccess: 'Registered successfully',
      invalidCredentials: 'Invalid email or password',
      signIn: 'Sign In',
      signUp: 'Sign Up',
      welcomeBack: 'Welcome Back',
      createAccount: 'Create New Account',
    },
    nav: {
      dashboard: 'Dashboard',
      courses: 'Courses',
      myCourses: 'My Courses',
      meetings: 'Meetings',
      recordings: 'Recordings',
      students: 'Students',
      teachers: 'Teachers',
      calendar: 'Calendar',
      schedule: 'Schedule',
      grades: 'Grades',
      settings: 'Settings',
      aiTeacher: 'AI Teacher',
      finance: 'Finance',
      library: 'Library',
      messages: 'Messages',
      profile: 'Profile',
      tasks: 'Tasks',
      academics: 'Academics',
      marketing: 'Marketing',
      school: 'School',
    },
    courses: {
      title: 'Courses',
      newCourse: 'New Course',
      courseName: 'Course Name',
      courseCode: 'Course Code',
      description: 'Description',
      students: 'Students',
      teachers: 'Teachers',
      startDate: 'Start Date',
      endDate: 'End Date',
      status: 'Status',
      draft: 'Draft',
      active: 'Active',
      archived: 'Archived',
      enrollments: 'Enrollments',
      sessions: 'Sessions',
      materials: 'Materials',
    },
    meetings: {
      title: 'Meetings',
      newMeeting: 'New Meeting',
      joinMeeting: 'Join Meeting',
      startMeeting: 'Start Meeting',
      endMeeting: 'End Meeting',
      scheduledStart: 'Scheduled for',
      host: 'Host',
      participants: 'Participants',
      recording: 'Recording',
      startRecording: 'Start Recording',
      stopRecording: 'Stop Recording',
      live: 'Live',
      scheduled: 'Scheduled',
      ended: 'Ended',
      camera: 'Camera',
      microphone: 'Microphone',
      shareScreen: 'Share Screen',
      chat: 'Chat',
      leaveRoom: 'Leave Room',
    },
    finance: {
      title: 'Finance',
      revenue: 'Revenue',
      expenses: 'Expenses',
      balance: 'Balance',
      invoices: 'Invoices',
      payments: 'Payments',
      subscription: 'Subscription',
      plan: 'Plan',
      billing: 'Billing',
      payNow: 'Pay Now',
      addToCart: 'Add to Cart',
      cart: 'Cart',
      checkout: 'Checkout',
      total: 'Total',
      subtotal: 'Subtotal',
      tax: 'Tax',
      discount: 'Discount',
      paymentMethod: 'Payment Method',
      creditCard: 'Credit Card',
      connectStripe: 'Connect Stripe Account',
      stripeConnected: 'Stripe Account Connected',
    },
    superAdmin: {
      title: 'Super Admin',
      schools: 'Schools',
      users: 'Users',
      subscriptions: 'Subscriptions',
      storage: 'Storage',
      auditLogs: 'Audit Logs',
      admins: 'Super Admins',
      platformSettings: 'Platform Settings',
      pricingPlans: 'Pricing Plans',
      createPlan: 'Create Plan',
      editPlan: 'Edit Plan',
      planName: 'Plan Name',
      monthlyPrice: 'Monthly Price',
      yearlyPrice: 'Yearly Price',
      features: 'Features',
      limits: 'Limits',
    },
    errors: {
      required: 'This field is required',
      invalidEmail: 'Invalid email address',
      minLength: 'Minimum {{min}} characters',
      maxLength: 'Maximum {{max}} characters',
      passwordMismatch: 'Passwords do not match',
      serverError: 'Server error, please try again later',
      networkError: 'Network error, check your connection',
      unauthorized: 'You are not authorized to perform this action',
      notFound: 'Page not found',
    },
  },
};

// Initialize i18n instance
i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      he: { translation: fallbackTranslations.he },
      en: { translation: fallbackTranslations.en },
    },
    fallbackLng: 'en',
    defaultNS: 'translation',
    supportedLngs: Object.keys(SUPPORTED_LANGUAGES),
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    // HTTP backend config for loading translations from API
    backend: {
      loadPath: '/api/translations/{{lng}}',
      allowMultiLoading: false,
    },
    // Don't wait for translations to load before rendering
    react: {
      useSuspense: false,
    },
  });

// Update document direction when language changes
i18n.on('languageChanged', (lng) => {
  const dir = getLanguageDirection(lng);
  document.documentElement.lang = lng;
  document.documentElement.dir = dir;

  // Store preference
  localStorage.setItem('i18nextLng', lng);
});

// Set initial direction (English is default)
const initialLang = i18n.language || 'en';
document.documentElement.lang = initialLang;
document.documentElement.dir = getLanguageDirection(initialLang);

export { i18n };
export default i18n;
